import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Identifiable, Resource } from "@planzone/dto";
import { useUpdateOrganizationalUnit } from "@planzone/view-models";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { ResourcesPicker } from "@common/resourcesPicker";
import { Props } from "./types";
import styles from "./styles.scss";

function withoutResources(
  resources: Identifiable<Resource>[],
  removedResources: Identifiable<Resource>[],
) {
  return resources.filter(
    (resource) => !removedResources.some(({ uuid }) => uuid === resource.uuid),
  );
}

function OrganizationalUnitEdition({ organizationalUnit, cancel }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, updateOrganizationalUnit } =
    useUpdateOrganizationalUnit();
  const initialResources = organizationalUnit.resources ?? [];
  const [name, setName] = useState(organizationalUnit.name);
  const [resources, setResources] = useState(initialResources);
  const trimmedName = name.trim();
  const assignedResources = withoutResources(resources, initialResources);
  const unassignedResources = withoutResources(initialResources, resources);
  const hasChanges =
    trimmedName !== organizationalUnit.name ||
    assignedResources.length > 0 ||
    unassignedResources.length > 0;

  return (
    <Modal
      header={t("updateOrganizationalUnit.title", {
        name: organizationalUnit.name,
      })}
      className={styles.organizationalUnitEdition}
      close={cancel}
    >
      <form
        className={styles.main}
        onSubmit={(e) => {
          e.preventDefault();

          !isRequestPending &&
            !!trimmedName &&
            hasChanges &&
            updateOrganizationalUnit({
              organizationalUnit,
              name: trimmedName,
              assignedResources,
              unassignedResources,
            });
        }}
      >
        <div className={styles.group}>
          <h3 className={styles.title}>{t("updateOrganizationalUnit.name")}</h3>
          <input
            className={styles.input}
            value={name}
            placeholder={t("obsPage.creation.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <ResourcesPicker
          title={t("updateOrganizationalUnit.resources", {
            total: resources.length,
          })}
          resources={resources}
          addResources={(added) =>
            setResources([...withoutResources(resources, added), ...added])
          }
          removeResources={(removed) =>
            setResources(withoutResources(resources, removed))
          }
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("updateOrganizationalUnit.cancel")}
          </Button>
          <Button
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={isRequestPending || !trimmedName || !hasChanges}
          >
            {t("updateOrganizationalUnit.confirm")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { OrganizationalUnitEdition };
